import React, { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  Alert,
  Button,
  Card,
  Col,
  Empty,
  Result,
  Row,
  Select,
  Space,
  Statistic,
  Tag,
  Typography,
  theme,
} from 'antd'
import {
  ArrowRightOutlined,
  BookOutlined,
  FundOutlined,
  RiseOutlined,
  StarFilled,
  StarOutlined,
  TeamOutlined,
  WarningOutlined,
} from '@ant-design/icons'
import { useNavigate } from 'react-router-dom'
import { useGetMenus } from '@/shared/api/client/menu-controller'
import { customInstance } from '@/shared/api/mutator/custom-instance'
import { useAuthStore } from '@/modules/auth'
import { useTheme } from '@/components/common/ThemeProvider'
import { useUserPreferencesStore } from '@/stores/user-preferences-store'
import {
  buildDashboardLinkCollections,
  flattenMenuLinks,
  getDashboardDefinition,
  getDashboardPresetOptions,
} from '@/pages/home/dashboard-config'
import type { PageCustomerDTO } from '@/shared/api/model'

const { Title, Paragraph, Text } = Typography

interface PortfolioAtRiskResponse {
  totalLoans?: number
  totalOutstandingPrincipal?: number
  loansInArrears?: number
  parRatios?: Record<number, number | string>
  classificationBreakdown?: Record<string, number>
}

interface DueLoanInfo {
  custNo?: string
  paidPrincipal?: number
  paidInterest?: number
  paidPenalty?: number
}

interface RepaymentDueResponse {
  loans?: DueLoanInfo[]
  total?: number
}

const formatCurrency = (value?: number) =>
  new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(value || 0)

const formatPercent = (value?: number | string) => `${((Number(value) || 0) * 100).toFixed(2)}%`

const HomePage: React.FC = () => {
  const navigate = useNavigate()
  const { token } = theme.useToken()
  const { dark } = useTheme()
  const user = useAuthStore((state) => state.user)
  const dashboardPreset = useUserPreferencesStore((state) => state.dashboardPreset)
  const setDashboardPreset = useUserPreferencesStore((state) => state.setDashboardPreset)
  const favoriteHomeLinks = useUserPreferencesStore((state) => state.favoriteHomeLinks)
  const setFavoriteHomeLinks = useUserPreferencesStore((state) => state.setFavoriteHomeLinks)
  const branch = user?.branch || user?.loginBranch
  const { data: apiMenus } = useGetMenus()

  const dashboardDefinition = useMemo(
    () => getDashboardDefinition(dashboardPreset, user?.role),
    [dashboardPreset, user?.role]
  )

  const menuLinks = useMemo(() => flattenMenuLinks((apiMenus as any[]) || []), [apiMenus])
  const linkCollections = useMemo(
    () => buildDashboardLinkCollections(dashboardDefinition, menuLinks, favoriteHomeLinks),
    [dashboardDefinition, favoriteHomeLinks, menuLinks]
  )

  const customersQuery = useQuery({
    queryKey: ['home-dashboard', 'customers', branch],
    enabled: !!branch,
    queryFn: () =>
      customInstance<PageCustomerDTO>({
        url: '/api/customer-management/search',
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        data: { branch, page: 0, size: 1 },
      }),
  })

  const portfolioRiskQuery = useQuery({
    queryKey: ['home-dashboard', 'portfolio-risk', branch],
    enabled: !!branch,
    queryFn: () => customInstance<PortfolioAtRiskResponse>({ url: `/api/loans/portfolio-risk/${branch}`, method: 'GET' }),
  })

  const dueLoansQuery = useQuery({
    queryKey: ['home-dashboard', 'due-loans', branch],
    enabled: !!branch,
    queryFn: () =>
      customInstance<RepaymentDueResponse>({
        url: '/api/loans/repay/due',
        method: 'GET',
        params: { branch, page: 0, size: 250 },
      }),
  })

  const dueLoans = dueLoansQuery.data?.loans || []
  const customersWithPastDues = new Set(dueLoans.map((loan) => loan.custNo).filter(Boolean)).size
  const customersYetToStartRepayment = new Set(
    dueLoans
      .filter((loan) => !(Number(loan.paidPrincipal) > 0 || Number(loan.paidInterest) > 0 || Number(loan.paidPenalty) > 0))
      .map((loan) => loan.custNo)
      .filter(Boolean)
  ).size

  const isLoading = customersQuery.isLoading || portfolioRiskQuery.isLoading || dueLoansQuery.isLoading
  const hasError = customersQuery.isError && portfolioRiskQuery.isError && dueLoansQuery.isError

  const toggleFavorite = (path: string) => {
    const next = favoriteHomeLinks.includes(path)
      ? favoriteHomeLinks.filter((item) => item !== path)
      : [...favoriteHomeLinks, path].slice(-8)
    setFavoriteHomeLinks(next)
  }

  if (!branch) {
    return <Result status="warning" title="Branch context is required" subTitle="Sign in again or switch branch to load the homepage dashboard." />
  }

  if (hasError) {
    return <Result status="error" title="Unable to load dashboard data" subTitle="The homepage could not retrieve branch metrics right now." />
  }

  return (
    <div style={{ padding: 24, background: dark ? token.colorBgBase : '#f5f7fb', minHeight: '100%' }}>
      <Space direction="vertical" size={16} style={{ width: '100%' }}>
        <Card>
          <Row gutter={[16, 16]} align="middle" justify="space-between">
            <Col xs={24} lg={16}>
              <Space direction="vertical" size={8}>
                <Title level={2} style={{ margin: 0 }}>{dashboardDefinition.title}</Title>
                <Paragraph style={{ margin: 0 }}>{dashboardDefinition.subtitle}</Paragraph>
                <Space wrap>
                  <Tag color="blue">{user?.branchName || branch}</Tag>
                  <Tag color="purple">{dashboardDefinition.audience}</Tag>
                  {user?.role ? <Tag>{user.role}</Tag> : null}
                </Space>
              </Space>
            </Col>
            <Col xs={24} lg={8}>
              <Space direction="vertical" style={{ width: '100%' }}>
                <Text type="secondary">Dashboard layout</Text>
                <Select value={dashboardPreset} options={getDashboardPresetOptions()} onChange={setDashboardPreset} />
              </Space>
            </Col>
          </Row>
        </Card>

        <Alert
          type="info"
          showIcon
          message="Configurable homepage foundation"
          description="This homepage uses a dashboard preset registry, so different user groups can receive different layouts later while still allowing individual preference overrides."
        />

        <Row gutter={[16, 16]}>
          <Col xs={24} md={12} xl={6}>
            <Card loading={isLoading}><Statistic title="Active customers" value={customersQuery.data?.totalElements || 0} prefix={<TeamOutlined />} /></Card>
          </Col>
          <Col xs={24} md={12} xl={6}>
            <Card loading={isLoading}><Statistic title="Portfolio at risk > 30" value={formatPercent(portfolioRiskQuery.data?.parRatios?.[30])} prefix={<FundOutlined />} /></Card>
          </Col>
          <Col xs={24} md={12} xl={6}>
            <Card loading={isLoading}><Statistic title="Customers with past dues" value={customersWithPastDues} prefix={<WarningOutlined />} /></Card>
          </Col>
          <Col xs={24} md={12} xl={6}>
            <Card loading={isLoading}><Statistic title="Past due, no repayment started" value={customersYetToStartRepayment} prefix={<RiseOutlined />} /></Card>
          </Col>
        </Row>

        <Row gutter={[16, 16]}>
          <Col xs={24} xl={10}>
            <Card title="Portfolio health" loading={isLoading}>
              <Space direction="vertical" size={16} style={{ width: '100%' }}>
                <Row gutter={[16, 16]}>
                  <Col span={8}><Statistic title="PAR > 1" value={formatPercent(portfolioRiskQuery.data?.parRatios?.[1])} /></Col>
                  <Col span={8}><Statistic title="PAR > 30" value={formatPercent(portfolioRiskQuery.data?.parRatios?.[30])} /></Col>
                  <Col span={8}><Statistic title="PAR > 90" value={formatPercent(portfolioRiskQuery.data?.parRatios?.[90])} /></Col>
                </Row>
                <Row gutter={[16, 16]}>
                  <Col span={12}><Statistic title="Loans in arrears" value={portfolioRiskQuery.data?.loansInArrears || 0} /></Col>
                  <Col span={12}><Statistic title="Outstanding principal" value={formatCurrency(Number(portfolioRiskQuery.data?.totalOutstandingPrincipal || 0))} /></Col>
                </Row>
                <Space wrap>
                  {Object.entries(portfolioRiskQuery.data?.classificationBreakdown || {}).map(([label, count]) => (
                    <Tag key={label} color="processing">{label}: {count}</Tag>
                  ))}
                </Space>
              </Space>
            </Card>
          </Col>
          <Col xs={24} xl={14}>
            <Card title="Quick context" loading={isLoading}>
              <Space direction="vertical" size={12} style={{ width: '100%' }}>
                <Paragraph style={{ margin: 0 }}>
                  The homepage focuses on customer scale, overdue exposure, and branch portfolio pressure so staff can quickly decide where to act first.
                </Paragraph>
                <Paragraph style={{ margin: 0 }}>
                  The most-used and important links below are assembled from the live menu tree, while favorites are persisted per user preference.
                </Paragraph>
              </Space>
            </Card>
          </Col>
        </Row>

        <Row gutter={[16, 16]}>
          {[
            { title: 'Favorites', links: linkCollections.favoriteLinks },
            { title: 'Important links', links: linkCollections.importantLinks },
            { title: 'Most used links', links: linkCollections.mostUsedLinks },
          ].map((section) => (
            <Col xs={24} lg={8} key={section.title}>
              <Card title={section.title}>
                <Space direction="vertical" size={12} style={{ width: '100%' }}>
                  {section.links.length === 0 ? (
                    <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={`No ${section.title.toLowerCase()} available`} />
                  ) : (
                    section.links.map((link) => (
                      <Card key={link.path} size="small">
                        <Row justify="space-between" align="middle" gutter={[8, 8]}>
                          <Col flex="auto">
                            <Space direction="vertical" size={2} style={{ width: '100%' }}>
                              <Text strong>{link.label}</Text>
                              <Text type="secondary">{link.description}</Text>
                              <Tag>{link.group}</Tag>
                            </Space>
                          </Col>
                          <Col>
                            <Space>
                              <Button type="text" icon={favoriteHomeLinks.includes(link.path) ? <StarFilled /> : <StarOutlined />} onClick={() => toggleFavorite(link.path)} />
                              <Button type="primary" icon={<ArrowRightOutlined />} onClick={() => navigate(link.path)}>
                                Open
                              </Button>
                            </Space>
                          </Col>
                        </Row>
                      </Card>
                    ))
                  )}
                </Space>
              </Card>
            </Col>
          ))}
        </Row>
      </Space>
    </div>
  )
}

export default HomePage
